import { useEffect, useEffectEvent } from 'react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router';
import { useTenant } from '../contexts/TenantContext';
import { getLastTenantSlug, clearLastTenantSlug } from '../utils/lastTenant';
import { TenantSelector } from '../pages/TenantSelector';

/**
 * Landing target for `/`. Two paths:
 *
 *   1. **Resume last tenant.** If localStorage has a `lastTenantSlug` AND
 *      that tenant is still in the operator's `tenants[]`, redirect to
 *      `/:slug/inbox`. One-click resume; no picker required after the
 *      first visit.
 *
 *   2. **Show TenantSelector.** First-time visit, stored slug is stale
 *      (revoked access), or operator only has one tenant — fall through
 *      to the picker. The picker handles 0/1/N tenant cases itself.
 *
 * Single tenant + auto-pick is intentionally handled inside `TenantSelector`
 * (not here) so the auto-redirect logic stays in one place.
 *
 * **Why a component, not inline in AppRoutes:** needs `useTenant` and the
 * stored-slug check; cleaner as a dedicated landing component than
 * inlining redirect logic into the route table.
 */
export function RootLanding() {
  const { tenants, loading } = useTenant();
  const navigate = useNavigate();

  const navigateReplace = useEffectEvent((path: string) => {
    navigate(path, { replace: true });
  });

  useEffect(() => {
    if (loading) return;
    const lastSlug = getLastTenantSlug();
    if (!lastSlug) return;

    const stillValid = tenants?.some((t) => t.slug === lastSlug);
    if (!stillValid) {
      // Operator lost access to the previously-used tenant. Clear the
      // memory so we don't keep trying to resume a tenant they can't see.
      clearLastTenantSlug();
      return;
    }
    navigateReplace(`/${lastSlug}/inbox`);
  }, [loading, tenants]);

  // While tenant data loads OR we're about to redirect, render nothing
  // (AppGate already shows the loading skeleton). When we land here without
  // a resumable tenant, fall through to TenantSelector — same component
  // mounted at `/tenants`.
  if (loading) return null;

  // If we just navigated (above), this component unmounts before showing.
  // If we didn't navigate, render the selector.
  return <TenantSelector />;
}

/**
 * `/:tenantSlug/pages` — the old page picker. The inbox has an "All pages" filter, so the extra step is gone;
 * old links land in the inbox and keep `?page=`.
 */
export function PagesRedirect() {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const { search } = useLocation();
  if (!tenantSlug) return <Navigate to="/" replace />;
  return <Navigate to={`/${tenantSlug}/inbox${search}`} replace />;
}

/**
 * Redirect helper for `/:tenantSlug` (without `/inbox`) — operator typed
 * the tenant URL without a sub-path. Land them in the inbox.
 */
export function TenantRootRedirect() {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  if (!tenantSlug) return <Navigate to="/" replace />;
  return <Navigate to={`/${tenantSlug}/inbox`} replace />;
}

/**
 * Redirect helper for the legacy `/:tenantSlug/:channel/:pageId` per-page
 * dashboard route. Map to the unified inbox with the page filter applied:
 * `/:slug/inbox?page=:pageId`. The channel falls out as inbox-side filter
 * (operators viewing one IG page only see one IG channel in chips anyway).
 *
 * Why preserve the legacy URL: bookmarks, external links, and any embedded
 * usage we don't know about. Redirect-not-404 keeps them working.
 */
export function LegacyPageDashboardRedirect() {
  const { tenantSlug, pageId } = useParams<{
    tenantSlug?: string;
    channel?: string;
    pageId?: string;
  }>();
  if (!tenantSlug) return <Navigate to="/" replace />;
  const qs = pageId ? `?page=${encodeURIComponent(pageId)}` : '';
  return <Navigate to={`/${tenantSlug}/inbox${qs}`} replace />;
}
